import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { Calendar } from "@/components/Calendar/Calendar";
import { useEffect, useMemo, useState } from "react";
import { ar, enUS } from "react-day-picker/locale";
import { useSellCarData } from "@/common/Contexts/SellCarDataContext";
import { useTranslation } from "@i18n";
import { format, getDate, getUnixTime } from "date-fns";
import { arSA } from "date-fns/locale/ar-SA";
import { twMerge } from "tailwind-merge";
import { Conditional } from "@componentsShared/Conditional/Conditional";
import { Then } from "@componentsShared/Conditional/Then";
import { Else } from "@componentsShared/Conditional/Else";
import FlowButton from "@/pages/sell-my-car/components/FlowButton";
import useIsMobile from "@hooks/useIsMobile";
import { submitDateTime } from "@/apis/sell-my-car";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useScreenNavigation } from "@/common/Contexts/ScreenNavigationContext";
import FLowLoadingSpinner from "@/pages/sell-my-car/components/FLowLoadingSpinner";
import { trackEvents } from "@/common/events/events";
import FLowFixedContainer from "@/pages/sell-my-car/components/FLowFixedContainer";
import { useLang } from "@/common/Contexts/LanguageContext";
import useHelpers from "@hooks/useHelpers";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
var CarBookingDateTime = function CarBookingDateTime() {
  var _state$draftOrder$cur3, _state$draftOrder$cur6, _state$draftOrder$cur7, _state$availableTimes2;
  var _useSellCarData = useSellCarData(),
    state = _useSellCarData.state,
    actions = _useSellCarData.actions;
  var _useState = useState(undefined),
    date = _useState[0],
    setDate = _useState[1];
  var _useState2 = useState(true),
    isLoading = _useState2[0],
    setIsLoading = _useState2[1];
  var _useState3 = useState(false),
    isSubmitting = _useState3[0],
    setIsSubmitting = _useState3[1];
  var _useTranslation = useTranslation(),
    tShort = _useTranslation.tShort;
  var _useState4 = useState(null),
    selectedTime = _useState4[0],
    setSelectedTime = _useState4[1];
  var isMobile = useIsMobile();
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var _useScreenNavigation = useScreenNavigation(),
    screenActions = _useScreenNavigation.actions;
  var navigate = useNavigate();
  var lang = useLang();
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl;
  var maxHeight = useMemo(function () {
    var _window;
    if (__IS_SSR__ || !isMobile) {
      return "fit-content";
    }
    return ((_window = window) === null || _window === void 0 ? void 0 : _window.innerHeight) - 160 - 88 + "px";
  }, []);

  // Only fetch step data on mount if date_and_time data is not already loaded
  useEffect(function () {
    var fetchData = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
        var _state$draftOrder$cur, _state$draftOrder$cur2;
        return _regenerator().w(function (_context) {
          while (1) switch (_context.n) {
            case 0:
              if (!((_state$draftOrder$cur = state.draftOrder.currentStep) !== null && _state$draftOrder$cur !== void 0 && (_state$draftOrder$cur2 = _state$draftOrder$cur.date_and_time) !== null && _state$draftOrder$cur2 !== void 0 && _state$draftOrder$cur2.available_days)) {
                _context.n = 1;
                break;
              }
              setIsLoading(false);
              return _context.a(2);
            case 1:
              setIsLoading(true);
              _context.n = 2;
              return actions.fetchStepData();
            case 2:
              setIsLoading(false);
            case 3:
              return _context.a(2);
          }
        }, _callee);
      }));
      return function fetchData() {
        return _ref.apply(this, arguments);
      };
    }();
    fetchData();
  }, []);

  // Get available days from context
  var dateAndTimeData = (_state$draftOrder$cur3 = state.draftOrder.currentStep) === null || _state$draftOrder$cur3 === void 0 ? void 0 : _state$draftOrder$cur3.date_and_time;

  // Convert available_days to Date objects for easier comparison
  var availableDates = useMemo(function () {
    if (!(dateAndTimeData !== null && dateAndTimeData !== void 0 && dateAndTimeData.available_days)) return [];
    return dateAndTimeData.available_days.map(function (day) {
      // Parse date_value (format: "2025-12-30")
      var _day$date_value$split = day.date_value.split('-').map(Number),
        _day$date_value$split2 = _slicedToArray(_day$date_value$split, 3),
        year = _day$date_value$split2[0],
        month = _day$date_value$split2[1],
        dayNum = _day$date_value$split2[2];
      return new Date(year, month - 1, dayNum);
    });
  }, [dateAndTimeData === null || dateAndTimeData === void 0 ? void 0 : dateAndTimeData.available_days]);

  // Set initial selected date and time from API
  useEffect(function () {
    if (dateAndTimeData !== null && dateAndTimeData !== void 0 && dateAndTimeData.selected_day && !date) {
      var _dateAndTimeData$sele = dateAndTimeData.selected_day.date_value.split('-').map(Number),
        _dateAndTimeData$sele2 = _slicedToArray(_dateAndTimeData$sele, 3),
        year = _dateAndTimeData$sele2[0],
        month = _dateAndTimeData$sele2[1],
        dayNum = _dateAndTimeData$sele2[2];
      var selectedDate = new Date(year, month - 1, dayNum);
      setDate(selectedDate);

      // Fetch times for the selected day
      var dayTimestamp = getUnixTime(selectedDate);
      actions.fetchRescheduleTimes(dayTimestamp);
    }
  }, [dateAndTimeData === null || dateAndTimeData === void 0 ? void 0 : dateAndTimeData.selected_day]);

  // Set initial selected time from API when available times are loaded
  useEffect(function () {
    var _state$availableTimes, _state$draftOrder$cur4, _state$draftOrder$cur5;
    if (((_state$availableTimes = state.availableTimes) === null || _state$availableTimes === void 0 ? void 0 : _state$availableTimes.length) > 0 && !selectedTime && (_state$draftOrder$cur4 = state.draftOrder.currentStep) !== null && _state$draftOrder$cur4 !== void 0 && (_state$draftOrder$cur5 = _state$draftOrder$cur4.data) !== null && _state$draftOrder$cur5 !== void 0 && _state$draftOrder$cur5.appointment_datetime) {
      // Parse the appointment_datetime to find matching time slot
      var appointmentDatetime = state.draftOrder.currentStep.data.appointment_datetime;

      // appointment_datetime is typically an ISO string or timestamp
      // Try to match it with one of the available times
      var appointmentTimestamp = new Date(appointmentDatetime).getTime() / 1000;
      var matchingTime = state.availableTimes.find(function (time) {
        // Check if the appointment time falls within this time slot
        return appointmentTimestamp >= time.from_timestamp && appointmentTimestamp <= time.to_timestamp;
      });
      if (matchingTime) {
        setSelectedTime(matchingTime);
      }
    }
  }, [state.availableTimes, selectedTime, (_state$draftOrder$cur6 = state.draftOrder.currentStep) === null || _state$draftOrder$cur6 === void 0 ? void 0 : (_state$draftOrder$cur7 = _state$draftOrder$cur6.data) === null || _state$draftOrder$cur7 === void 0 ? void 0 : _state$draftOrder$cur7.appointment_datetime]);

  // Create disabled matcher - disable past dates and unavailable dates
  var disabledMatcher = useMemo(function () {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    return [
    // Disable dates before today
    {
      before: today
    },
    // Disable dates not in available_days
    function (day) {
      var isAvailable = availableDates.some(function (availableDate) {
        return availableDate.getFullYear() === day.getFullYear() && availableDate.getMonth() === day.getMonth() && availableDate.getDate() === day.getDate();
      });
      return !isAvailable;
    }];
  }, [availableDates]);

  // Get the start month (current month)
  var startMonth = useMemo(function () {
    var today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }, []);

  // Get the end month (2 months from the last available date)
  var endMonth = useMemo(function () {
    if (availableDates.length === 0) {
      // Default to 3 months from now if no available dates
      var future = new Date();
      future.setMonth(future.getMonth() + 2);
      return future;
    }

    // Find the maximum date from available dates
    var maxDate = availableDates.reduce(function (max, current) {
      return current > max ? current : max;
    }, availableDates[0]);

    // Add 2 months buffer
    var futureDate = new Date(maxDate);
    futureDate.setMonth(futureDate.getMonth() + 2);
    return futureDate;
  }, [availableDates]);
  var formatArabicTime = function formatArabicTime(timestamp) {
    // Convert Unix timestamp (seconds) to milliseconds and create Date object
    var date = new Date(timestamp * 1000);

    // Format using date-fns with Arabic locale
    var time = format(date, "hh:mm a", {
      locale: arSA
    });
    if (lang == 'ar') {
      return time.replace("ص", "صباحًا").replace("م", "مساءً");
    }
    return time.replace("ص", "AM").replace("م", "PM");
  };
  var handleSubmit = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      var requestId, dateAtMidnight, datestamp, payload, response, _stepData$current_ste, stepData, currentStepKey, _state$draftOrder, newURL, _t;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.p = _context2.n) {
          case 0:
            if (!(!date || !selectedTime)) {
              _context2.n = 1;
              break;
            }
            console.error("Date or time not selected");
            return _context2.a(2);
          case 1:
            requestId = searchParams.get("request_id");
            if (requestId) {
              _context2.n = 2;
              break;
            }
            console.error("No request_id found");
            return _context2.a(2);
          case 2:
            // Get datestamp (date at midnight)
            dateAtMidnight = new Date(date);
            dateAtMidnight.setHours(0, 0, 0, 0);
            datestamp = getUnixTime(dateAtMidnight);
            payload = {
              request_id: requestId,
              rescheduled_datestamp: datestamp,
              rescheduled_from_timestamp: selectedTime.from_timestamp,
              rescheduled_to_timestamp: selectedTime.to_timestamp,
              rescheduling_reason_id: 0,
              is_reschedule: 0
            };
            setIsSubmitting(true);
            _context2.p = 3;
            _context2.n = 4;
            return submitDateTime(payload);
          case 4:
            response = _context2.v;
            if (!response) {
              _context2.n = 6;
              break;
            }
            _context2.n = 5;
            return actions.fetchStepData();
          case 5:
            stepData = _context2.v;
            currentStepKey = stepData === null || stepData === void 0 ? void 0 : (_stepData$current_ste = stepData.current_step) === null || _stepData$current_ste === void 0 ? void 0 : _stepData$current_ste.step_key;
            trackEvents("sell_car_appointment_select", {
              request_id: requestId,
              booking_date: getDate(datestamp),
              booking_time: getUnixTime(datestamp),
              platform: getPlatformInfo(),
              version: "3"
            });
            if (currentStepKey) {
              // TODO: Implement history management here
              newURL = createUrl("/site/sell-my-car", {
                request_id: state === null || state === void 0 ? void 0 : (_state$draftOrder = state.draftOrder) === null || _state$draftOrder === void 0 ? void 0 : _state$draftOrder.requestId
              });
              window.history.replaceState({}, null, newURL);
              screenActions.navigateToScreen(currentStepKey);
              // Navigate to the new URL
              navigate(newURL, {
                replace: true
              });
            }
          case 6:
            _context2.n = 8;
            break;
          case 7:
            _context2.p = 7;
            _t = _context2.v;
            console.error("Error submitting date time:", _t);
            // TODO: Show error message to user
          case 8:
            _context2.p = 8;
            setIsSubmitting(false);
            return _context2.f(8);
          case 9:
            return _context2.a(2);
        }
      }, _callee2, null, [[3, 7, 8, 9]]);
    }));
    return function handleSubmit() {
      return _ref2.apply(this, arguments);
    };
  }();
  if (isLoading || state.draftOrder.isLoadingStepData) {
    return /*#__PURE__*/React.createElement("div", {
      className: "py-6 px-4"
    }, /*#__PURE__*/React.createElement("div", {
      className: "py-2 px-4 border border-solid border-gray-echo rounded-lg"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center justify-center h-64"
    }, /*#__PURE__*/React.createElement(FLowLoadingSpinner, null))));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-screen",
    className: "h-full relative",
    style: {
      maxHeight: maxHeight,
      overflowY: "auto"
    }
  }, /*#__PURE__*/React.createElement("form", {
    id: "sell-car-booking-form",
    onSubmit: handleSubmit,
    className: "min-h-[calc(100%-72px)]"
  }, /*#__PURE__*/React.createElement("div", {
    className: "py-6 px-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-calendar-container",
    className: "py-2 px-4 border border-solid border-gray-echo rounded-lg"
  }, /*#__PURE__*/React.createElement(Calendar, {
    mode: "single",
    locale: lang == 'ar' ? ar : enUS,
    dir: lang == 'ar' ? 'rtl' : 'ltr',
    weekStartsOn: 0,
    className: "w-full p-0",
    selected: date,
    onSelect: (/*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3(selected) {
        var dayTimestamp;
        return _regenerator().w(function (_context3) {
          while (1) switch (_context3.n) {
            case 0:
              if (!selected) {
                _context3.n = 2;
                break;
              }
              setDate(selected);
              dayTimestamp = getUnixTime(selected);
              _context3.n = 1;
              return actions.fetchRescheduleTimes(dayTimestamp);
            case 1:
              setSelectedTime(null);
            case 2:
              return _context3.a(2);
          }
        }, _callee3);
      }));
      return function (_x) {
        return _ref3.apply(this, arguments);
      };
    }()),
    buttonVariant: "ghost",
    disabled: disabledMatcher,
    today: new Date(),
    startMonth: startMonth,
    endMonth: endMonth
  }), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-calendar-legend",
    className: "flex items-center justify-between py-2 px-3 md:py-4 md:justify-start md:gap-6"
  }, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-legend-today",
    className: "flex items-center gap-2 md:min-w-[250px]"
  }, /*#__PURE__*/React.createElement("p", {
    className: "w-3 h-3 rounded-full bg-[#12B0FF]"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-[#00143C] font-medium text-xs"
  }, tShort("sell_trade_v2/color_today", {
    ns: "web_sell_trade"
  }))), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-legend-available",
    className: "flex items-center gap-2 md:min-w-[250px]"
  }, /*#__PURE__*/React.createElement("p", {
    className: "w-3 h-3 rounded-full bg-primary-new"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-[#00143C] font-medium text-xs"
  }, tShort("sell_trade_v2/color_available", {
    ns: "web_sell_trade"
  }))), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-legend-unavailable",
    className: "flex items-center gap-2 md:min-w-[250px]"
  }, /*#__PURE__*/React.createElement("p", {
    className: "w-3 h-3 rounded-full bg-[#B0B0B0]"
  }), /*#__PURE__*/React.createElement("p", {
    className: "text-[#00143C] font-medium text-xs"
  }, tShort("sell_trade_v2/color_not_available", {
    ns: "web_sell_trade"
  }))))), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-times-section",
    className: "flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("h4", {
    id: "sell-car-booking-times-title",
    className: "text-black-primary font-bold"
  }, tShort("sell_trade_v2/available_times", {
    ns: "web_sell_trade"
  })), /*#__PURE__*/React.createElement(Conditional, {
    condition: state === null || state === void 0 ? void 0 : state.isLoadingTimes
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-times-loading",
    className: "grid grid-cols-3 gap-3 md:grid-cols-4"
  }, Array.from({
    length: isMobile ? 6 : 8
  }).map(function (_, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: "placeholder" + i,
      className: "rounded-lg p-2 border border-solid border-gray-echo text-center font-semibold text-base leading-6 bg-white animate-pulse cursor-default"
    }, /*#__PURE__*/React.createElement("div", {
      className: "h-4 w-full bg-gray-echo rounded"
    }));
  }))), /*#__PURE__*/React.createElement(Else, null, /*#__PURE__*/React.createElement(Conditional, {
    condition: ((_state$availableTimes2 = state.availableTimes) === null || _state$availableTimes2 === void 0 ? void 0 : _state$availableTimes2.length) > 0
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-times-list",
    className: "grid grid-cols-3 gap-3 md:grid-cols-4"
  }, state.availableTimes.map(function (time, index) {
    return /*#__PURE__*/React.createElement("div", {
      key: time.from_timestamp,
      id: "sell-car-booking-time-option-".concat(index),
      className: twMerge("rounded-lg p-2 border border-solid border-gray-echo text-center font-semibold text-base leading-6 text-black-primary cursor-pointer transition-colors", (selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.from_timestamp) === time.from_timestamp && "text-primary-new border-primary-new bg-[#E7E7F8]", !time.is_active && "opacity-50 cursor-not-allowed"),
      onClick: function onClick() {
        if (time.is_active) {
          setSelectedTime(time);
        }
      }
    }, formatArabicTime(time.from_timestamp));
  })), /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-waiting-note",
    className: "p-2.5 rounded-[5px] outline outline-1 outline-offset-[-1px] bg-[#F2F2F2] inline-flex flex-col justify-center items-center gap-2.5"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-full h-full text-start text-black-primary text-xs font-bold font-['Cairo'] leading-6 md:text-center"
  }, tShort("sell_trade_v2/waiting_time_note", {
    ns: "web_sell_trade"
  })))), /*#__PURE__*/React.createElement(Else, null, /*#__PURE__*/React.createElement("div", {
    id: "sell-car-booking-no-times",
    className: "py-3 w-full flex items-center justify-center rounded bg-[#FDEFEB]"
  }, /*#__PURE__*/React.createElement("p", {
    className: "max-w-[265px] text-center font-medium text-base text-[#EC6234]"
  }, tShort("sell_trade_v2/no_available_time_slots")))))))), /*#__PURE__*/React.createElement(FLowFixedContainer, null, /*#__PURE__*/React.createElement("div", {
    className: "md:relative md:max-w-[340px] md:mx-auto md:gap-5 md:!mt-[90px]"
  }, /*#__PURE__*/React.createElement(FlowButton, {
    id: "sell-car-booking-submit-btn",
    type: "button",
    disabled: isSubmitting || !date || !selectedTime || (state === null || state === void 0 ? void 0 : state.isLoadingTimes),
    className: "w-full md:min-w-[360px]",
    onClick: handleSubmit
  }, /*#__PURE__*/React.createElement(Conditional, {
    condition: isSubmitting || (state === null || state === void 0 ? void 0 : state.isLoadingTimes)
  }, /*#__PURE__*/React.createElement(Then, null, /*#__PURE__*/React.createElement(FLowLoadingSpinner, null)), /*#__PURE__*/React.createElement(Else, null, tShort("sell_trade_v2/submit", {
    ns: "web_sell_trade"
  })))))))))));
};
export default CarBookingDateTime;